import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { useChatStatus } from '@field-tickets/shared';
import { AuthGuard } from '@/components/AuthGuard';
import { ComplianceRoute } from '@/components/compliance/ComplianceRoute';
import { RequireCapability } from '@/components/RequireCapability';
import { RequireAnyCapability } from '@/components/RequireAnyCapability';
import { AppLayout } from '@/layouts/AppLayout';
import { Spinner } from '@/components/Spinner';
import { ToastContainer } from '@/components/Toast';
import { PwaRefreshPrompt } from '@/components/PwaRefreshPrompt';
import { useOfflineQueueReplay } from '@/hooks/useOfflineQueueReplay';

// Public routes (keep eagerly loaded for fast first paint)
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { NoAccessPage } from '@/pages/NoAccessPage';
import { PendingApprovalPage } from '@/pages/PendingApprovalPage';

const CHUNK_RELOAD_STORAGE_KEY = 'alturex-work:chunk-reload-at';
const CHUNK_RELOAD_THROTTLE_MS = 30_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const message = String((error as { message?: unknown }).message || '').toLowerCase();
  const name = String((error as { name?: unknown }).name || '').toLowerCase();

  return (
    name === 'chunkloaderror'
    || message.includes('failed to fetch dynamically imported module')
    || message.includes('error loading dynamically imported module')
    || message.includes('importing a module script failed')
    || message.includes('loading chunk')
    || message.includes('unable to preload css')
  );
}

function shouldHardReloadForChunkError(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY) || 0);
    const now = Date.now();
    if (now - lastReloadAt < CHUNK_RELOAD_THROTTLE_MS) return false;
    window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(now));
    return true;
  } catch {
    return true;
  }
}

function hardReloadAfterChunkError(error: unknown): boolean {
  if (!isChunkLoadError(error) || !shouldHardReloadForChunkError()) return false;

  console.warn('[ChunkLoad] Stale route chunk detected; reloading app shell', {
    route: window.location.pathname,
    message: error instanceof Error ? error.message : String(error),
  });
  window.location.reload();
  return true;
}

// Retry wrapper for lazy imports — retries twice, then refreshes once on stale chunks
export function lazyWithRetry<T extends React.ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
  retries = 2,
): React.LazyExoticComponent<T> {
  return lazy(() => {
    const attempt = (remaining: number): Promise<{ default: T }> =>
      factory().catch((err: unknown) => {
        if (remaining <= 0) {
          if (hardReloadAfterChunkError(err)) {
            return new Promise<never>(() => {});
          }
          throw err;
        }
        return new Promise<{ default: T }>((resolve) =>
          setTimeout(() => resolve(attempt(remaining - 1)), 1000),
        );
      });
    return attempt(retries);
  });
}

// Error boundary for route load/render failures — shows retry UI instead of a blank route
export class ChunkErrorBoundary extends React.Component<
  { children: React.ReactNode; resetKey?: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; resetKey?: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ChunkErrorBoundary] Route failed to load or render', {
      route: this.props.resetKey || window.location.pathname,
      userAgent: window.navigator.userAgent,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="login-page">
          <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-2)' }}>
            Connection issue
          </h1>
          <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>
            This page failed to load. Check your connection and try again.
          </p>
          <button
            className="login-btn"
            onClick={() => window.location.reload()}
            style={{ border: 'none', cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

type RouteImporter = () => Promise<{ default: React.ComponentType<unknown> }>;

const loadSearchPage: RouteImporter = () => import('@/pages/SearchPage').then(m => ({ default: m.SearchPage }));
const loadCatalogPage: RouteImporter = () => import('@/pages/CatalogPage').then(m => ({ default: m.CatalogPage }));
const loadBoardPage: RouteImporter = () => import('@/pages/BoardPage').then(m => ({ default: m.BoardPage }));
const loadSalesPage: RouteImporter = () => import('@/pages/SalesPage').then(m => ({ default: m.SalesPage }));
const loadCustomersPage: RouteImporter = () => import('@/pages/CustomersPage').then(m => ({ default: m.CustomersPage }));
const loadVendorsPage: RouteImporter = () => import('@/pages/VendorsPage').then(m => ({ default: m.VendorsPage }));
const loadPurchaseOrdersPage: RouteImporter = () => import('@/pages/PurchaseOrdersPage').then(m => ({ default: m.PurchaseOrdersPage }));

const DIRECTORY_ROUTE_IMPORTERS = [
  loadSearchPage,
  loadCatalogPage,
  loadBoardPage,
  loadSalesPage,
  loadCustomersPage,
  loadVendorsPage,
  loadPurchaseOrdersPage,
] as const;

/** Warm directory chunks after first paint so their search inputs mount promptly. */
export function scheduleDirectoryRoutePrefetch(
  importers: readonly RouteImporter[] = DIRECTORY_ROUTE_IMPORTERS,
): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const warm = () => {
    importers.forEach((load) => {
      void load().catch(() => undefined);
    });
  };
  const idleWindow = window as Window & {
    requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (idleWindow.requestIdleCallback) {
    const handle = idleWindow.requestIdleCallback(warm, { timeout: 2_000 });
    return () => idleWindow.cancelIdleCallback?.(handle);
  }

  const handle = window.setTimeout(warm, 100);
  return () => window.clearTimeout(handle);
}

// Lazy-loaded auth-protected pages
const DashboardPage = lazyWithRetry(() => import('@/pages/DashboardPage').then(m => ({ default: m.DashboardPage })));
const MyTicketsPage = lazyWithRetry(() => import('@/pages/MyTicketsPage').then(m => ({ default: m.MyTicketsPage })));
const SiteRedirect = lazyWithRetry(() => import('@/pages/SiteRedirect').then(m => ({ default: m.SiteRedirect })));
const SiteViewPage = lazyWithRetry(() => import('@/pages/SiteViewPage').then(m => ({ default: m.SiteViewPage })));
const TicketEditPage = lazyWithRetry(() => import('@/pages/TicketEditPage').then(m => ({ default: m.TicketEditPage })));
const SearchPage = lazyWithRetry(loadSearchPage);
const SettingsPage = lazyWithRetry(() => import('@/pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const TeamListPage = lazyWithRetry(() => import('@/pages/TeamListPage').then(m => ({ default: m.TeamListPage })));
const TeamMemberViewPage = lazyWithRetry(() => import('@/pages/TeamMemberViewPage').then(m => ({ default: m.TeamMemberViewPage })));
const TeamDashboardPage = lazyWithRetry(() => import('@/pages/TeamDashboardPage').then(m => ({ default: m.TeamDashboardPage })));
const AdminPage = lazyWithRetry(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const CatalogPage = lazyWithRetry(loadCatalogPage);
const BoardPage = lazyWithRetry(loadBoardPage);
// TicketKanbanPage shelved 2026-04-30 — replaced by /sites BoardPage workflow.
// Routes that previously rendered it (/kanban, /tickets/board, /board) now
// redirect to /sites. Re-enable the import + routes if reinstating.
const ReviewQueuePage = lazyWithRetry(() => import('@/pages/ReviewQueuePage').then(m => ({ default: m.ReviewQueuePage })));
const SalesPage = lazyWithRetry(loadSalesPage);
const SalesDocumentPage = lazyWithRetry(() => import('@/pages/SalesDocumentPage').then(m => ({ default: m.SalesDocumentPage })));
const SalesBuilderPage = lazyWithRetry(() => import('@/pages/SalesBuilderPage').then(m => ({ default: m.SalesBuilderPage })));
const InvoiceBuilderPage = lazyWithRetry(() => import('@/pages/InvoiceBuilderPage').then(m => ({ default: m.InvoiceBuilderPage })));
const CustomersPage = lazyWithRetry(loadCustomersPage);
const CustomerDetailPage = lazyWithRetry(() => import('@/pages/CustomerDetailPage').then(m => ({ default: m.CustomerDetailPage })));
const VendorsPage = lazyWithRetry(loadVendorsPage);
const PurchaseOrdersPage = lazyWithRetry(loadPurchaseOrdersPage);
const PurchaseOrderPage = lazyWithRetry(() => import('@/pages/PurchaseOrderPage').then(m => ({ default: m.PurchaseOrderPage })));
const PurchaseOrderNewPage = lazyWithRetry(() => import('@/pages/PurchaseOrderNewPage').then(m => ({ default: m.PurchaseOrderNewPage })));
const PurchaseOrderPrintPage = lazyWithRetry(() => import('@/pages/PurchaseOrderPrintPage').then(m => ({ default: m.PurchaseOrderPrintPage })));
const NotificationsPage = lazyWithRetry(() => import('@/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const ReportsPage = lazyWithRetry(() => import('@/pages/ReportsPage').then(m => ({ default: m.ReportsPage })));
const ChatPage = lazyWithRetry(() => import('@/pages/ChatPage').then(m => ({ default: m.ChatPage })));
const SafetyPage = lazyWithRetry(() => import('@/pages/SafetyPage').then(m => ({ default: m.SafetyPage })));
const MyDocumentsPage = lazyWithRetry(() => import('@/pages/MyDocumentsPage').then(m => ({ default: m.MyDocumentsPage })));

// Lazy-loaded modal
const TicketModal = lazyWithRetry(() => import('@/components/tickets/TicketModal').then(m => ({ default: m.TicketModal })));

function PageFallback() {
  return (
    <div className="page-loading">
      <Spinner />
    </div>
  );
}

function LazyPage({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ChunkErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<PageFallback />}>{children}</Suspense>
    </ChunkErrorBoundary>
  );
}

function RequireChatFeature({ children }: { children: React.ReactNode }) {
  const status = useChatStatus();
  if (status.isLoading && !status.data) {
    return <PageFallback />;
  }
  if (status.data?.capabilities.canViewChat !== true) {
    return <Navigate to="/no-access" replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}

/** Separated so useLocation() can be called inside BrowserRouter */
function AppRoutes() {
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location } | null;

  // Replay any offline-queued ticket autosaves on load + when connectivity returns.
  useOfflineQueueReplay();
  useEffect(() => scheduleDirectoryRoutePrefetch(), []);

  // If we have a background location, render routes against that
  // so the board/site page stays visible behind the modal
  const routeLocation = state?.backgroundLocation || location;

  return (
    <>
      <Routes location={routeLocation}>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/no-access" element={<NoAccessPage />} />
        <Route path="/pending-approval" element={<PendingApprovalPage />} />

        {/* Root redirect — before auth guard so it always works */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Auth-protected routes */}
        <Route element={<AuthGuard />}>
          {/* Printable PO renders OUTSIDE the app shell so no chrome reaches the paper. */}
          <Route path="/purchase-orders/:poId/print" element={<RequireCapability capability="canManagePurchaseOrders"><LazyPage><PurchaseOrderPrintPage /></LazyPage></RequireCapability>} />
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<LazyPage><DashboardPage /></LazyPage>} />
            <Route path="/my-tickets" element={<LazyPage><MyTicketsPage /></LazyPage>} />
            <Route path="/cards" element={<Navigate to="/sites" replace />} />
            <Route path="/cards/:cardId" element={<LazyPage><SiteRedirect /></LazyPage>} />
            <Route path="/sites/:siteId" element={<LazyPage><SiteViewPage /></LazyPage>} />
            <Route path="/tickets/:ticketId" element={<LazyPage><TicketEditPage /></LazyPage>} />
            <Route path="/search" element={<LazyPage><SearchPage /></LazyPage>} />
            <Route path="/settings" element={<LazyPage><SettingsPage /></LazyPage>} />
            <Route path="/team" element={<RequireCapability capability="canViewTeam"><LazyPage><TeamListPage /></LazyPage></RequireCapability>} />
            <Route path="/team/:techMemberId" element={<RequireCapability capability="canViewTeam"><LazyPage><TeamMemberViewPage /></LazyPage></RequireCapability>} />
            <Route path="/team-dashboard" element={<RequireCapability capability="canViewTeam"><LazyPage><TeamDashboardPage /></LazyPage></RequireCapability>} />
            <Route path="/admin" element={<RequireCapability capability="canManageMembers"><LazyPage><AdminPage /></LazyPage></RequireCapability>} />
            <Route path="/catalog" element={<LazyPage><CatalogPage /></LazyPage>} />
            <Route path="/sites" element={<LazyPage><BoardPage /></LazyPage>} />
            {/* Shelved /board, /kanban, /tickets/board — redirect to /sites */}
            <Route path="/board" element={<Navigate to="/sites" replace />} />
            <Route path="/kanban" element={<Navigate to="/sites" replace />} />
            <Route path="/tickets/board" element={<Navigate to="/sites" replace />} />
            <Route path="/review" element={<RequireCapability capability="canViewReviewQueue"><LazyPage><ReviewQueuePage /></LazyPage></RequireCapability>} />
            <Route path="/sales" element={<RequireCapability capability="canViewSales"><LazyPage><SalesPage /></LazyPage></RequireCapability>} />
            <Route path="/sales/estimate/:docId" element={<RequireCapability capability="canViewSales"><LazyPage><SalesDocumentPage /></LazyPage></RequireCapability>} />
            <Route path="/sales/invoice/:docId" element={<RequireCapability capability="canViewSales"><LazyPage><SalesDocumentPage /></LazyPage></RequireCapability>} />
            <Route path="/sales/builder/:draftId" element={<RequireCapability capability="canViewSales"><LazyPage><SalesBuilderPage /></LazyPage></RequireCapability>} />
            <Route path="/sales/invoice-builder/:draftId" element={<RequireAnyCapability capabilities={['canConvertEstimate', 'canManageInvoices', 'canCreateInvoices']}><LazyPage><InvoiceBuilderPage /></LazyPage></RequireAnyCapability>} />
            {/* Same canViewSales gate the customer record already carries — the
                directory list is only being made link-addressable, not opened up. */}
            <Route path="/customers" element={<RequireCapability capability="canViewSales"><LazyPage><CustomersPage /></LazyPage></RequireCapability>} />
            <Route path="/customers/:qbCustomerId" element={<RequireCapability capability="canViewSales"><LazyPage><CustomerDetailPage /></LazyPage></RequireCapability>} />
            <Route path="/vendors" element={<RequireCapability capability="canManagePurchaseOrders"><LazyPage><VendorsPage /></LazyPage></RequireCapability>} />
            <Route path="/purchase-orders" element={<RequireAnyCapability capabilities={['canManagePurchaseOrders', 'canReceiveInventory']}><LazyPage><PurchaseOrdersPage /></LazyPage></RequireAnyCapability>} />
            <Route path="/purchase-orders/new" element={<RequireCapability capability="canManagePurchaseOrders"><LazyPage><PurchaseOrderNewPage /></LazyPage></RequireCapability>} />
            <Route path="/purchase-orders/:poId" element={<RequireAnyCapability capabilities={['canManagePurchaseOrders', 'canReceiveInventory']}><LazyPage><PurchaseOrderPage /></LazyPage></RequireAnyCapability>} />
            <Route path="/notifications" element={<LazyPage><NotificationsPage /></LazyPage>} />
            <Route path="/reports" element={<RequireCapability capability="canViewRates"><LazyPage><ReportsPage /></LazyPage></RequireCapability>} />
            <Route path="/chat" element={<RequireChatFeature><LazyPage><ChatPage /></LazyPage></RequireChatFeature>} />
            <Route path="/safety" element={<ComplianceRoute need="read"><LazyPage><SafetyPage /></LazyPage></ComplianceRoute>} />
            <Route path="/my-documents" element={<ComplianceRoute need="self"><LazyPage><MyDocumentsPage /></LazyPage></ComplianceRoute>} />
          </Route>
        </Route>

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Modal overlay — rendered when navigated from a background location */}
      {state?.backgroundLocation && (
        <Routes>
          <Route
            path="/tickets/:ticketId"
            element={
              <ChunkErrorBoundary resetKey={location.pathname}>
                <Suspense fallback={null}>
                  <TicketModal />
                </Suspense>
              </ChunkErrorBoundary>
            }
          />
        </Routes>
      )}

      {/* Toast notifications — must be mounted once so showToast() has an active ref */}
      <ToastContainer />

      {/* PWA update banner — mounted once so showRefreshPrompt() has an active ref */}
      <PwaRefreshPrompt />
    </>
  );
}

function NotFoundPage() {
  return (
    <div className="login-page">
      <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-2)' }}>404</h1>
      <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>Page not found</p>
      <a href="/dashboard" className="login-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>
        Go to Dashboard
      </a>
    </div>
  );
}
